import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { CustomTabMenuItem } from 'src/app/shared/models/custom-tab-menu-item';
import { HHRALogicService } from './human-health-risk-assessment.logic.service';
import { Message } from 'primeng/api';
import { Project } from 'src/app/shared/models/project';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { Environmental_Assessments, UserLogicService } from 'src/app/shared/services/user.logic.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Constants } from 'src/app/shared/utils/constants';
import { ModelLibrary, ModelLibraryCollection } from 'src/app/shared/models/model-library';
import { firstValueFrom, take } from 'rxjs';
import { DisciplineApiService } from 'src/app/shared/services/echo/discipline.api.service';
import { Discipline } from 'src/app/shared/models/echo/discipline';
import { DataSetApiService } from 'src/app/shared/services/echo/data-set.api.service';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { ModelLibraryApiService } from 'src/app/shared/services/model-library.api.service';
import { HHEASetupColDef } from './hhea-setup-coldef';
import { SetupLogicService } from 'src/app/shared/services/setup.logic.service';
import { UsHandlerExposureLogicService } from 'src/app/shared/services/human-health-us-handler-exposure-logic.service';


@Component({
  selector: 'app-human-health-risk-assessment',
  templateUrl: './human-health-risk-assessment.component.html',
  styleUrls: ['./human-health-risk-assessment.component.css']
})
export class HumanHealthRiskAssessmentComponent implements OnInit, OnDestroy{

  featureAcronym = Environmental_Assessments.HHRA
  activeItem!: CustomTabMenuItem;
  message!: Message;
  loading: boolean = false;
  nextIndex?: number;
  selectedProject?: Project;
  selectedProjectStatus?: number;
  isSetupValid: boolean = false;
  destroyRef = inject(DestroyRef)
  isProjectOwnershipValid: boolean = true;
  disciplinePk: number = 0;
  compartments: string[] = [Constants.COMPARTMENTS.DIETARY.toLowerCase(), Constants.COMPARTMENTS.NONDIETARY.toLowerCase(), Constants.COMPARTMENTS.CORE.toLowerCase()];
  columnDef: any[] = [];
  modelsCollection: ModelLibraryCollection[] = [];
  compartmentList: any[] = [];
  environmentalAssessments = Environmental_Assessments.HHRA;
  private setupLogicService = inject(SetupLogicService);
  

  constructor(public HHRALogicService: HHRALogicService,
    private selectedProjectApiService: SelectedProjectApiService,
    private disciplineApiService: DisciplineApiService,
    private userLogicService: UserLogicService,
    private orderByPipe: OrderByPipe,
    private modelLibraryApiService: ModelLibraryApiService,
    private HHEASetupColDef: HHEASetupColDef,
    private dataSetService: DataSetApiService,
    private cdr: ChangeDetectorRef,
    private usHandlerExposureLogicService: UsHandlerExposureLogicService,
  ) { }

  selectedGeographyEffect = effect(() => {
    if(this.setupLogicService.selectedGeographyKey())
      this.initColumnDef();
  });

  async ngOnInit(): Promise<void> {
    this.loading = true;
    await this.initCompartemtList();
    await this.getDiscipline();
    await this.initModels();
    this.initSubscribes();
  }

  async initCompartemtList(): Promise<void> {
    await firstValueFrom(this.dataSetService.getCompartments(Constants.CONST_COMPARTMENT).pipe(take(1))).then(((compartment: any) => {
      this.compartmentList = compartment;
    }));
  }

  async getDiscipline(): Promise<void> {
    await firstValueFrom(this.disciplineApiService.getDisciplineByName(Constants.DISCIPLINE_IDENTITIES.TOXICOLOGY).pipe(take(1))).then((discipline: Discipline) => {
      this.disciplinePk = discipline?.disciplinePk ?? -1;
    });
  }

  onActiveIndexChange(index: number) {
    this.nextIndex = index;
    if(this.nextIndex!=5){
        this.usHandlerExposureLogicService.setUSHandlerInputsCombinations([]);
    }
    this.HHRALogicService.setActiveIndexChanged(true, index);
  }

  initSubscribes(): void {
    this.selectedProjectApiService.selectedProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project?: Project) => {
      this.selectedProject = project;
      this.isProjectOwnershipValid = this.selectedProject ? this.userLogicService.verifyProjectOwnership(this.selectedProject?.createdBy, this.featureAcronym) : true;
      this.onActiveIndexChange(0);
    });

    this.selectedProjectApiService.selectedProjectStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status?: number) => {
      this.selectedProjectStatus = status;
    });

    this.HHRALogicService.activeItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(activeItem => {
      this.activeItem = activeItem
    });

    this.HHRALogicService.isLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isLoading => {
      this.loading = isLoading;
    });

    this.HHRALogicService.success.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(success => {
      this.loading = false;
      if (success) {
        const nextItem = this.HHRALogicService.items.find(i => i.index == this.nextIndex);
        if (nextItem)
          this.HHRALogicService.setActiveItem(nextItem);
      }
    });
  }

  async initModels() {
    let localCollection: ModelLibraryCollection[] = [];
    let models: ModelLibrary[] = [];
    this.compartments.forEach(compartment => {
      localCollection.push({ name: compartment, library: [] });
    });

    await firstValueFrom(this.modelLibraryApiService.getModels()).then((library: ModelLibrary[]) => {
      let dietary = localCollection.find(x => x.name.toLowerCase() === Constants.COMPARTMENTS.DIETARY.toLowerCase())
      let nonDietary = localCollection.find(x => x.name.toLowerCase() === Constants.COMPARTMENTS.NONDIETARY.toLowerCase());
      models = library.filter(f => f.compartmentXModel?.length && f.compartmentXModel?.length >= 0 && f.disciplineXModel?.some(d => d == this.disciplinePk));
      if (dietary) dietary.library = this.orderByPipe.transform(models.filter(m => m.compartmentXModel?.includes(this.compartmentList?.find(x => x.compartment.toLowerCase() === Constants.COMPARTMENTS.DIETARY.toLowerCase())?.endpointCompartmentPk)), 'nameVersion');
      if (nonDietary) nonDietary.library = this.orderByPipe.transform(models.filter(m => m.compartmentXModel?.includes(this.compartmentList?.find(x => x.compartment.replace("-", "").toLowerCase() === Constants.COMPARTMENTS.NONDIETARY.toLowerCase())?.endpointCompartmentPk)), 'nameVersion');
    });
    this.modelsCollection = localCollection;
  }

  initColumnDef() {
    const selectedGeography = this.setupLogicService.selectedGeographyKey();
    let dietary = this.modelsCollection.find(x => x.name.toLowerCase() === Constants.COMPARTMENTS.DIETARY.toLowerCase())?.library.filter(m => m.geographyXModel?.some(g => this.setupLogicService.isSelectedGeography(g, selectedGeography)));
    let nonDietary = this.modelsCollection.find(x => x.name.toLowerCase() === Constants.COMPARTMENTS.NONDIETARY.toLowerCase())?.library.filter(m => m.geographyXModel?.some(g => this.setupLogicService.isSelectedGeography(g, selectedGeography)));
    this.columnDef = this.HHEASetupColDef.getColumnsDefinition(dietary ?? [], nonDietary ?? [], this.isProjectOwnershipValid);
  }

  validateSetup(isSetupValid: boolean) {
    const copyItems = [...this.HHRALogicService.items];
    copyItems[1].label = this.selectedProject?.geography == Constants.GEOGRAPHYS.USA ? Constants.COMPARTMENTS.TOX_EXPO_INPUTS : Constants.COMPARTMENTS.SUBSTANCE_PROPERTIES;
    copyItems[1].visible = isSetupValid;
    copyItems[2].visible = isSetupValid;
    copyItems[3].visible = isSetupValid && this.selectedProject?.geography != Constants.GEOGRAPHYS.USA;
    copyItems[4].visible = isSetupValid;
    copyItems[5].visible = isSetupValid;
    this.HHRALogicService.items = copyItems;
    this.isSetupValid = isSetupValid;
    this.cdr.detectChanges();
  }

  public getIsValidApplicationScheme(isValid: boolean): void {
    this.isSetupValid = isValid;
    this.cdr.detectChanges();
  }

  validateRunProjectTab(): void {
    const originalItems = [...this.HHRALogicService.items];
    let runProjectItem = originalItems.find(x => x.label === Constants.HUMAN_HEALTH_RISK_ASSESSMENT_STEPS[4].label);
    if(runProjectItem)
      runProjectItem.disabled = (this.selectedProject && this.selectedProject.status === Constants.PROJECT_STATUS_ID.IN_PROGRESS);
    this.HHRALogicService.items = originalItems;
  }

  ngOnDestroy(): void {
    this.selectedGeographyEffect.destroy();
  }

  validateQCResult(result: boolean): void {
    const originalItems = [...this.HHRALogicService.items];
    let runProjectItem = originalItems.find(x => x.label === Constants.HUMAN_HEALTH_RISK_ASSESSMENT_STEPS[5].label);
    runProjectItem!.disabled = result;
    this.HHRALogicService.items = originalItems;
  }
}
